/**
 * The audit trail, readable at last.
 *
 * **The permission existed and guarded nothing.** `read_audit_log` has been defined since
 * M0 and granted to owner, admin and auditor, and no endpoint enforced it because no
 * endpoint read the log. The chain was written faithfully, hashed, and reachable only with
 * a database shell — so on a platform whose central claim is defensible provenance, "who
 * exported this report, and when" could not be answered from the product.
 *
 * The screen is built around the questions people actually arrive with — who touched this
 * case, who changed a role, who approved that merge — which is why the filters are actor,
 * action, resource and time rather than a free-text box over a hash-chained table.
 *
 * There is no control here that writes anything, and there must never be one.
 */

import { useState, type ReactElement } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { AuditEntry, AuditFilters, ChainStatus, Paged } from '@/lib/schemas';
import { Badge, Button, Card, ErrorState, Loading, Table } from '@/components/ui';

/** The audit trail view. */
export function AuditPage(): ReactElement {
  const [action, setAction] = useState('');
  const [outcome, setOutcome] = useState('');
  const [resourceType, setResourceType] = useState('');

  const filters = useQuery({
    queryKey: ['audit-filters'],
    queryFn: () => request<AuditFilters>('/audit/filters'),
  });

  // Verification is a separate request because it is O(chain) where a page is O(page).
  // Folding it into the listing would make reading the log quadratic in its size.
  const status = useQuery({
    queryKey: ['audit-chain-status'],
    queryFn: () => request<ChainStatus>('/audit/chain-status'),
  });

  const entries = useInfiniteQuery({
    queryKey: ['audit', action, outcome, resourceType],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      request<Paged<AuditEntry>>('/audit', {
        query: {
          limit: 50,
          cursor: pageParam,
          action: action || undefined,
          outcome: outcome || undefined,
          resource_type: resourceType || undefined,
        },
      }),
    getNextPageParam: (last) => last.page.next_cursor ?? undefined,
  });

  if (entries.isLoading) return <Loading />;
  if (entries.error) return <ErrorState error={entries.error} />;

  const rows = entries.data?.pages.flatMap((page) => page.data) ?? [];
  const resourceTypes = [...new Set(rows.map((row) => row.resource_type).filter(Boolean))];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-lg font-semibold">Audit trail</h1>
        {/* The testid sits on the wrapper: `Badge` takes only `children` and `tone`, so a
            `data-*` prop passed to it is silently dropped. */}
        {status.data && (
          <span data-testid="chain-status" className="flex items-center gap-2 text-xs text-muted">
            {status.data.entries} entries
            <Badge tone={status.data.intact ? 'ok' : 'danger'}>
              {status.data.intact ? 'chain intact' : `chain broken at ${status.data.broken_at.join(', ')}`}
            </Badge>
          </span>
        )}
      </header>

      <p className="max-w-3xl text-sm text-muted">
        Every action this workspace has taken, hash-chained so that a missing or altered
        entry is detectable rather than merely unlikely. Read-only by design — there is no
        way to amend it from here, and an audit log with an edit button is not an audit log.
      </p>

      <Card>
        <div className="flex flex-wrap items-end gap-3 p-3">
          <label className="block text-xs">
            <span className="text-muted">Action</span>
            <select
              data-testid="audit-action"
              value={action}
              onChange={(event) => setAction(event.target.value)}
              className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Any action</option>
              {(filters.data?.actions ?? []).map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="text-muted">Resource</span>
            <select
              data-testid="audit-resource"
              value={resourceType}
              onChange={(event) => setResourceType(event.target.value)}
              className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Any resource</option>
              {resourceTypes.map((value) => (
                <option key={value} value={value ?? ''}>
                  {value}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs">
            <span className="text-muted">Outcome</span>
            <select
              data-testid="audit-outcome"
              value={outcome}
              onChange={(event) => setOutcome(event.target.value)}
              className="mt-1 block rounded border border-line bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Any outcome</option>
              <option value="success">success</option>
              <option value="failure">failure</option>
            </select>
          </label>
        </div>
      </Card>

      <Table
        headers={['#', 'When', 'Actor', 'Action', 'Resource', 'Outcome', 'Detail']}
        empty="Nothing matches these filters."
        rows={rows.map((entry) => [
          <span key="s" className="font-mono text-xs text-muted">
            {entry.sequence}
          </span>,
          <span key="t" className="text-xs">
            {entry.occurred_at.slice(0, 19).replace('T', ' ')}
          </span>,
          <span key="a" className="font-mono text-xs">
            {entry.actor_id ? entry.actor_id.slice(0, 8) : entry.actor_type}
          </span>,
          <span key="c" className="font-mono text-xs" data-testid="audit-action-cell">
            {entry.action}
          </span>,
          <span key="r" className="text-xs text-muted">
            {entry.resource_type ? `${entry.resource_type} ${entry.resource_id?.slice(0, 8) ?? ''}` : '—'}
          </span>,
          <Badge key="o" tone={entry.outcome === 'success' ? 'ok' : 'danger'}>
            {entry.outcome}
          </Badge>,
          <span key="d" className="break-all font-mono text-xs text-muted">
            {Object.keys(entry.detail).length ? JSON.stringify(entry.detail) : '—'}
          </span>,
        ])}
      />

      {entries.hasNextPage && (
        <Button
          data-testid="load-more-audit"
          disabled={entries.isFetchingNextPage}
          onClick={() => void entries.fetchNextPage()}
        >
          {entries.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}
